import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashLayout from "@/components/DashLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export default function LeavesTimesheet() {
  const { user } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [lv, setLv] = useState({ from_date: "", to_date: "", reason: "", applicant_type: user?.role === "parent" ? "parent" : "teacher" });
  const [ts, setTs] = useState({ date: new Date().toISOString().slice(0,10), hours: 8, task: "" });

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/leaves"), api.get("/timesheet")]);
    setLeaves(a.data); setSheets(b.data);
  };
  useEffect(() => { load(); }, []);

  const applyLeave = async (e) => {
    e.preventDefault();
    try { await api.post("/leaves", lv); toast.success("Leave applied"); setLv({ ...lv, from_date: "", to_date: "", reason: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addTs = async (e) => {
    e.preventDefault();
    try { await api.post("/timesheet", { ...ts, hours: Number(ts.hours) }); toast.success("Logged"); setTs({ ...ts, task: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setStatus = async (id, status) => { await api.put(`/leaves/${id}/status`, { status }); load(); };

  const isAdmin = user?.role === "school_admin" || user?.role === "super_admin";

  return (
    <DashLayout variant="school" title="Leaves & Timesheet">
      <Tabs defaultValue="leaves">
        <TabsList>
          <TabsTrigger value="leaves" data-testid="tab-leaves">Leave Apply</TabsTrigger>
          <TabsTrigger value="timesheet" data-testid="tab-timesheet">Timesheet</TabsTrigger>
        </TabsList>

        <TabsContent value="leaves">
          <Card className="p-6 max-w-2xl mb-6">
            <form onSubmit={applyLeave} className="grid md:grid-cols-2 gap-4" data-testid="leave-form">
              <div><Label className="mb-1 block text-xs">From</Label><Input type="date" required value={lv.from_date} onChange={(e) => setLv({ ...lv, from_date: e.target.value })} data-testid="lv-from" /></div>
              <div><Label className="mb-1 block text-xs">To</Label><Input type="date" required value={lv.to_date} onChange={(e) => setLv({ ...lv, to_date: e.target.value })} data-testid="lv-to" /></div>
              <div className="md:col-span-2"><Label className="mb-1 block text-xs">Reason</Label><Textarea rows={3} required value={lv.reason} onChange={(e) => setLv({ ...lv, reason: e.target.value })} data-testid="lv-reason" /></div>
              <div className="md:col-span-2"><Button type="submit" className="bg-blue-700 hover:bg-blue-800" data-testid="lv-submit"><Plus className="w-4 h-4 mr-2" />Apply Leave</Button></div>
            </form>
          </Card>
          <div className="bg-white border rounded-lg">
            <Table><TableHeader><TableRow><TableHead>Applicant</TableHead><TableHead>Dates</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead>{isAdmin && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
              <TableBody>{leaves.map(l => (
                <TableRow key={l.id} data-testid={`row-leave-${l.id}`}>
                  <TableCell className="font-semibold">{l.user_name}<div className="text-xs text-slate-500">{l.applicant_type}</div></TableCell>
                  <TableCell className="text-sm">{l.from_date} → {l.to_date}</TableCell>
                  <TableCell className="text-sm max-w-md">{l.reason}</TableCell>
                  <TableCell><Badge variant={l.status === "approved" ? "default" : l.status === "rejected" ? "destructive" : "outline"}>{l.status}</Badge></TableCell>
                  {isAdmin && <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => setStatus(l.id, "approved")} data-testid={`approve-lv-${l.id}`}>Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => setStatus(l.id, "rejected")} data-testid={`reject-lv-${l.id}`}>Reject</Button>
                  </TableCell>}
                </TableRow>
              ))}</TableBody></Table>
          </div>
        </TabsContent>

        <TabsContent value="timesheet">
          <Card className="p-6 max-w-2xl mb-6">
            <form onSubmit={addTs} className="grid md:grid-cols-3 gap-4 items-end" data-testid="ts-form">
              <div><Label className="mb-1 block text-xs">Date</Label><Input type="date" required value={ts.date} onChange={(e) => setTs({ ...ts, date: e.target.value })} data-testid="ts-date" /></div>
              <div><Label className="mb-1 block text-xs">Hours</Label><Input type="number" step="0.5" required value={ts.hours} onChange={(e) => setTs({ ...ts, hours: e.target.value })} data-testid="ts-hours" /></div>
              <div><Label className="mb-1 block text-xs">Task</Label><Input required value={ts.task} onChange={(e) => setTs({ ...ts, task: e.target.value })} data-testid="ts-task" /></div>
              <Button type="submit" className="bg-blue-700 hover:bg-blue-800 md:col-span-3 justify-self-start" data-testid="ts-submit"><Plus className="w-4 h-4 mr-2" />Add Entry</Button>
            </form>
          </Card>
          <div className="bg-white border rounded-lg">
            <Table><TableHeader><TableRow><TableHead>User</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Hours</TableHead><TableHead>Task</TableHead></TableRow></TableHeader>
              <TableBody>{sheets.map(s => (
                <TableRow key={s.id}><TableCell className="font-semibold">{s.user_name}</TableCell><TableCell>{s.date}</TableCell><TableCell className="text-right tabular-nums">{s.hours}</TableCell><TableCell>{s.task}</TableCell></TableRow>
              ))}</TableBody></Table>
          </div>
        </TabsContent>
      </Tabs>
    </DashLayout>
  );
}
